// Minimal `document.cookie` helpers for client-side UI-preference cookies (theme, language).
//
// These run only on the client and no-op during SSR: the server resolves the same cookies straight
// from the request headers (see lib/server-theme.ts and lib/server-locale.ts), so there is no need to
// read them through a React provider. This replaces the former `react-cookie` dependency.
//
// Values are session-scoped at the app root ({ path: '/' }, SameSite=Lax) — matching the attributes
// `react-cookie` produced here — so the existing server-side readers keep seeing the same cookie.

export interface CookieOptions {
  /** Lifetime in seconds. Omitted → session cookie (expires with the browser session). */
  maxAge?: number
  /** Cookie domain attribute (e.g. `.danchiano.com`). Omitted → the exact current host. */
  domain?: string
}

export function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined
  // eslint-disable-next-line security/detect-non-literal-regexp -- callers pass module-constant names
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`))
  if (match?.[1] === undefined) return undefined
  try {
    return decodeURIComponent(match[1])
  } catch {
    // A malformed percent-escape (e.g. `language=es%`) must not throw: this runs at module init via
    // app-store's initializer, so a bad cookie would white-screen the app. Fall back to the raw value,
    // matching what the replaced react-cookie/universal-cookie did.
    return match[1]
  }
}

export function setCookie(name: string, value: string, options?: CookieOptions): void {
  if (typeof document === 'undefined') return
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : ''
  const maxAge = options?.maxAge === undefined ? '' : `; max-age=${String(options.maxAge)}`
  const domain = options?.domain ? `; domain=${options.domain}` : ''
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/${maxAge}${domain}; SameSite=Lax${secure}`
}

export function deleteCookie(name: string, options?: Pick<CookieOptions, 'domain'>): void {
  if (typeof document === 'undefined') return
  const domain = options?.domain ? `; domain=${options.domain}` : ''
  document.cookie = `${name}=; path=/; max-age=0${domain}; SameSite=Lax`
}
